import { useCallback } from 'react';
import { hrTermOn } from '@/lib/utils/work-days/work-days';
import {
  useGetHrTermsOf,
  useGetMyHrTerms,
} from '@/modules/time-attendance/attendance/hooks/use-attendance';
import { scheduledWorkSecondsOn } from '@/modules/time-attendance/attendance/services/attendance-rules';
import { TimeSheetTask } from '../types/timesheet.types';

export type DayTarget = 'under' | 'onTarget';

/** All hours logged on a day (billable and non-billable), in decimal hours. */
export const loggedHours = (tasks: readonly TimeSheetTask[]) =>
  tasks.reduce(
    (sum, task) =>
      sum + (Number(task.Billable) || 0) + (Number(task.GeneralAdmin) || 0) + (Number(task.SalesAndMarketing) || 0),
    0
  );

/**
 * Whether a logged day is Under time or On target (HR Module Prototype, Component Map › Day status;
 * agreed 2026-11-02): its hours against that weekday's working hours in the employee's HR term in force
 * on the day. `null` while the HR terms load, or for a day without working hours (nothing to compare).
 * The signed-in employee's own days by default, or those of `userId`.
 */
export const useDayTarget = (userId?: string) => {
  const scheduledHours = useScheduledHours(userId);
  return useCallback(
    (day: string, tasks: readonly TimeSheetTask[]): DayTarget | null => {
      const scheduled = scheduledHours(day);
      if (scheduled === null) return null;
      return loggedHours(tasks) < scheduled ? 'under' : 'onTarget';
    },
    [scheduledHours]
  );
};

/**
 * A day's working hours (decimal) in the HR term in force on it, for the signed-in employee or
 * `userId`; `null` while the HR terms load or on a day without working hours.
 */
export const useScheduledHours = (userId?: string) => {
  const mine = useGetMyHrTerms();
  const theirs = useGetHrTermsOf(userId ?? '');
  const { data: hrTerms } = userId ? theirs : mine;
  return useCallback(
    (day: string): number | null => {
      if (!hrTerms || !day) return null;
      const scheduled = scheduledWorkSecondsOn(hrTermOn(hrTerms, day)?.WorkDays, day);
      return scheduled > 0 ? scheduled / 3600 : null;
    },
    [hrTerms]
  );
};

/** Decimal hours as "h:mm" (8.5 → "8:30"). */
export const formatHours = (hours: number) => {
  const minutes = Math.round((Number(hours) || 0) * 60);
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
};
